import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';

import { handleGeneralDocumentPreview } from '../../../../helpers/filePreviewHelper';
import { GeneralDocumentsBannerProps } from './types';
import {
  Banner,
  DropdownMenu,
  Headline,
  HeadlineAndSubheadlineContainer,
  LinkButton,
  LinkButtonContainer,
  LogoContainer,
  MenuButton,
  MenuItem,
  SubHeadline,
} from './styles';
import {
  cropConnectApiClient,
  CropConnectRoleDefinitions,
  GeneralDocumentDto,
  GeneralDocumentLightDto,
} from '../../../../services/cropConnectApiClient';
import { securityTrimConditionsMet } from '../../../../services/auth/SecurityTrim';
import { useContextStore } from '../../../../stores/contextStore';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { API_CONFIG } from '../../../../services/apiConfig';
import GeneralDocumentBannerIcon from '../../../../assets/icons/GeneralDocumentBannerIcon';

const GeneralDocumentBanner: React.FC<GeneralDocumentsBannerProps> = ({
  bannerWidget,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
}) => {
  const { t } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [generalDocuments, setGeneralDocuments] = useState<
    GeneralDocumentDto[] | GeneralDocumentLightDto[]
  >([]);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const {
    globalAssignment,
    nordzuckerInternalUser,
    businessPartnerAssignment,
    isAccessOnBehalf,
    isContextReady,
  } = useContextStore();

  const [isAdminView, setIsAdminView] = useState(
    !isAccessOnBehalf &&
      securityTrimConditionsMet(
        {
          requiredGlobalRoles: [CropConnectRoleDefinitions.DocumentPublisher],
        },
        globalAssignment,
        businessPartnerAssignment,
        nordzuckerInternalUser
      )
  );

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${API_CONFIG.BASE_URL}/Widgets/widgets/${bannerWidget.accessId}/generalDocuments/${generalDocuments[0].accessId}`
      );
      alert('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Handle clicks outside the menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    const getGeneralDocuments = async () => {
      if (bannerWidget?.accessId === undefined) return;
      if (
        !isAccessOnBehalf &&
        securityTrimConditionsMet(
          {
            requiredGlobalRoles: [CropConnectRoleDefinitions.DocumentPublisher],
          },
          globalAssignment,
          businessPartnerAssignment,
          nordzuckerInternalUser
        )
      ) {
        const generalDocuments =
          await cropConnectApiClient.getAllGeneralDocumentsForParent(
            bannerWidget.accessId
          );
        setGeneralDocuments(generalDocuments.data);
      } else {
        const generalDocuments = await cropConnectApiClient.getGeneralDocuments(
          bannerWidget.accessId
        );
        setGeneralDocuments(generalDocuments.data);
      }
    };
    if (isContextReady()) {
      setTimeout(() => {
        setIsAdminView(
          !isAccessOnBehalf &&
            securityTrimConditionsMet(
              {
                requiredGlobalRoles: [
                  CropConnectRoleDefinitions.DocumentPublisher,
                ],
              },
              globalAssignment,
              businessPartnerAssignment,
              nordzuckerInternalUser
            )
        );
      }, 2000);
      getGeneralDocuments();
    }
  }, [bannerWidget]);

  if (!generalDocuments || generalDocuments.length === 0) {
    return <></>;
  }

  return (
    <Banner
      $color={bannerWidget.propertyBag?.color}
      onClick={() =>
        handleGeneralDocumentPreview(
          bannerWidget.accessId,
          generalDocuments[0].accessId
        )
      }
    >
      {isAdminView && (
        <MenuButton
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
        >
          <MoreVertical color="black" size={20} />
        </MenuButton>
      )}

      {isMenuOpen && (
        <DropdownMenu ref={menuRef}>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onCopyLink();
            }}
          >
            {t(TranslationKeys.general_document_copy_link)}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onMoveUp();
            }}
          >
            {t(TranslationKeys.general_document_move_up)}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onMoveDown();
            }}
          >
            {t(TranslationKeys.general_document_move_down)}
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onEdit();
            }}
          >
            {t(TranslationKeys.general_document_edit)}
          </MenuItem>
          <MenuItem
            className="delete"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(false);
              onDelete();
            }}
          >
            {t(TranslationKeys.general_document_delete)}
          </MenuItem>
        </DropdownMenu>
      )}
      <LogoContainer>
        <GeneralDocumentBannerIcon />
      </LogoContainer>
      <HeadlineAndSubheadlineContainer>
        <Headline>
          {
            bannerWidget.propertyBag?.headlines.find(
              (headline) => headline.type === 'HEADLINE'
            )?.text
          }
        </Headline>
        <SubHeadline>
          {
            bannerWidget.propertyBag?.headlines.find(
              (headline) => headline.type === 'SUB_HEADLINE'
            )?.text
          }
        </SubHeadline>
      </HeadlineAndSubheadlineContainer>
      <LinkButtonContainer>
        <LinkButton>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </LinkButton>
      </LinkButtonContainer>
    </Banner>
  );
};

export default GeneralDocumentBanner;
